import { GetItemCounts } from '@ukpostbox/client-data';
import { useInfiniteForwardingBatches } from '@ukpostbox/client-data/lib/Wrappers/ReactQuery/Endpoints/ForwardingBatches';
import {
	PageHeader,
	ContentBlock,
	InfiniteLoadTrigger,
	ItemRowHeader,
	RichEmptyState,
	useLayout,
	ContentSidebarLayout,
	StackHorizontal,
	FlexGap,
	ItemRowHeaderCell,
	ItemRowCell,
	useTranslation,
} from '@ukpostbox/frontend-framework';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router';
import { ItemRowAddon } from '@ukpostbox/frontend-framework/lib/Components/ItemRow/ItemRowAddon';
import ForwardingBatchItem from '../../Mailboxes/Components/ForwardingBatchEntries/ForwardingBatchItem';
import { MailBoxImage } from '../../Mailboxes/Images/MailboxImage';
import MailboxNavigation from '../../Mailboxes/Components/MailboxNavigation';
import { useSite } from '../../_Global/Providers/SiteInterfaceProvider';
import { MailboxActions } from '../../Mailboxes/Components/MailboxActions';
import { MailboxLoader } from '../../Mailboxes/Components/Loaders/MailboxLoader';
import { useMailbox } from '../../Mailboxes/Providers/NewMailboxProvider';
import ForwardingBatchGridViewItem from '../../Mailboxes/Components/ForwardingBatchEntries/ForwardingBatchGridViewItem';
import { MailboxGridSize } from '../../Mailboxes/Components/Toolbars/Controls/MailboxGridSize';

function ForwardingBatches() {
	const location = useLocation();
	const { t } = useTranslation();
	const { isTiny, isSmall, isMedium, isLarge } = useLayout();
	const { currentLocale, updatePageMeta } = useSite();
	const { gridSize, gridView } = useMailbox();

	const [itemCounts, setItemCounts] = useState<any>({ loaded: 0, total: 0 });

	const {
		status,
		data,
		error,
		isFetchingNextPage,
		fetchNextPage,
		hasNextPage,
		dataUpdatedAt,
		isStale,
		isRefetching,
	} = useInfiniteForwardingBatches(location.state?.status ? { status: parseInt(location.state.status, 10) } : {});

	const mailBoxesRef = useRef<any>(null);
	const mailBoxNavigationRef = useRef<any>(null);

	useEffect(() => {
		if (data) {
			setItemCounts(GetItemCounts(data as any));
		}
	}, [dataUpdatedAt, data]);

	useEffect(() => {
		updatePageMeta({
			title: `${t('forwardingBatches.title')} - ${t('mailboxes.title')}`,
		});
	}, [currentLocale]);

	return (
		<ContentSidebarLayout
			ref={mailBoxesRef}
			navigationComponent={
				<MailboxNavigation
					ref={mailBoxNavigationRef}
					title={t('forwardingBatches.title')}
					addonContent={(isTiny || isSmall || isMedium) && <MailboxActions />}
				/>
			}
			extraContentClass={`flex-column forwarding-batch-rows ${
				gridView ? `item-content-files ${gridSize.className}` : `item-content-list`
			}`}
		>
			{!isTiny && !isSmall && !isMedium && (
				<PageHeader title={t('forwardingBatches.title')} secondaryHeader extraClass='mailbox-page-header'>
					<StackHorizontal gap={FlexGap.Small}>
						<MailboxActions />
					</StackHorizontal>
				</PageHeader>
			)}

			<ItemRowHeader
				title='Small Forwarding Batch List Header'
				sticky
				extraClass={`forwarding-batches-item-row ${gridView ? 'item-row-actions-active' : ''}`}
			>
				{gridView ? (
					<>
						<ItemRowCell />
						<ItemRowAddon extraClass='ml-a'>
							<MailboxGridSize />
						</ItemRowAddon>
					</>
				) : (
					<>
						<ItemRowHeaderCell />
						<ItemRowHeaderCell>{t('forwardingBatches.headings.batchId')}</ItemRowHeaderCell>
						{!isTiny && <ItemRowHeaderCell>{t('forwardingBatches.headings.status')}</ItemRowHeaderCell>}
						<ItemRowHeaderCell>{t('forwardingBatches.headings.lastChanged')}</ItemRowHeaderCell>
						{!isTiny && !isSmall && (
							<ItemRowHeaderCell>{t('forwardingBatches.headings.address')}</ItemRowHeaderCell>
						)}
						{!isTiny && !isSmall && !isMedium && (
							<ItemRowHeaderCell>{t('forwardingBatches.headings.postage')}</ItemRowHeaderCell>
						)}
						{!isTiny && !isSmall && !isMedium && !isLarge && (
							<ItemRowHeaderCell>{t('forwardingBatches.headings.trackingIds')}</ItemRowHeaderCell>
						)}
					</>
				)}
			</ItemRowHeader>

			{status === 'pending' ? (
				<MailboxLoader />
			) : status === 'error' ? (
				<ContentBlock error title={error?.name as string} content={error?.message} extraClass='m-md' />
			) : (
				<>
					{data.pages?.map((group) => {
						if (group.totalRecords === 0) {
							return (
								<RichEmptyState
									title={t('forwardingBatches.noData.title')}
									description={t('forwardingBatches.noData.description')}
									media={<MailBoxImage />}
								/>
							);
						}

						return group.data?.map((forwardingBatch) => {
							if (gridView) {
								return (
									<ForwardingBatchGridViewItem
										key={`forwardingBatchGridItem${forwardingBatch.batchId}`}
										{...forwardingBatch}
									/>
								);
							}

							return (
								<ForwardingBatchItem
									key={`forwardingBatch${forwardingBatch.batchId}`}
									{...forwardingBatch}
								/>
							);
						});
					})}

					{itemCounts.total > 0 && (
						<InfiniteLoadTrigger
							loaded={itemCounts.loaded}
							totalCount={itemCounts.total}
							hasNextPage={hasNextPage}
							fetchNextPage={fetchNextPage}
							isStale={isStale}
							isRefetching={isRefetching}
							isFetchingNextPage={isFetchingNextPage}
						/>
					)}
				</>
			)}
		</ContentSidebarLayout>
	);
}

export default ForwardingBatches;
